import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from "@remix-run/deno";
import {
  Link,
  Links,
  LiveReload,
  Meta,
  NavLink,
  Outlet,
  Scripts,
  ScrollRestoration } from
"@remix-run/react";
import React, { useEffect } from "react";
import { useBindings } from "./hooks/use-bindings.ts";

import FontAwesome from "./components/font-awesome.tsx";

import { RequestContext } from "./lib/request-context.ts";
import { render } from "./route-helpers/render.ts";

import cssReset from "./styles/reset.css";
import cssBase from "./styles/base.css";
import siteHeader from "./styles/site-header.css";
import nav from "./styles/nav.css";
import formElements from "./styles/form-elements.css";
import trayMenu from "./styles/tray-menu.css";
import siteFooter from "./styles/site-footer.css";
import { PublicEnv } from "./components/public-env.tsx";
export function links() {
  return [
  { rel: "stylesheet", href: cssReset },
  { rel: "stylesheet", href: cssBase },
  { rel: "stylesheet", href: formElements },
  { rel: "stylesheet", href: siteHeader },
  { rel: "stylesheet", href: siteFooter },
  { rel: "stylesheet", href: nav },
  { rel: "stylesheet", href: trayMenu },
  {
    rel: "stylesheet",
    href:
    "https://fonts.googleapis.com/css?family=Noto+Serif:,400,400i,700,700i%7CRoboto:300,400,400i,700,700i,900%7COswald:700,700i,400,400i,300,300i%7CArapey:400,400i"
  },
  {
    rel: "icon",
    href: "/favicon.ico",
    type: "image/png"
  }];

}

export const loader = async (env: LoaderFunctionArgs) => {
  return render(new RequestContext(env.request), {
    ident: true,
    requestUrl: true,
    rootUrl: true,
    ENV: {
      SENTRY_DSN: Deno.env.get("SENTRY_DSN") || "",
      STRIPE_PUBLIC_KEY: Deno.env.get("STRIPE_PUBLIC_KEY") || "",
      APP_ENV: environment(),
      APP_VERSION: version()
    }
  });
};

export const meta: MetaFunction<typeof loader> = (args) => {
  return [
  { charset: "utf-8" },
  { title: "TPM: Talking Points Memo - Accounts" },
  // { viewport: "width=device-width, initial-scale=1.0" },
  {
    property: "og:title",
    content: "TPM: Talking Points Memo - Accounts"
  },
  {
    name: "description",
    content: "TPM: Talking Points Memo - Accounts"
  },
  {
    name: "sentry-trace",
    content: args.data?.sentryTrace ?? ""
  },
  { name: "baggage", content: args.data?.sentryBaggage ?? "" }];

};

import { isRouteErrorResponse, useRouteError } from "@remix-run/react";
import SiteHeader from "./components/site-header.tsx";
import SiteFooter from "./components/site-footer.tsx";
import { environment, version } from "./lib/doess.ts";
import GTM from "~/components/gtm.tsx";
import Parsely from "~/components/parsely.tsx";

export function ErrorBoundary() {
  const error = useRouteError();
  let heading = "Something went wrong";
  let message = "An error occurred";

  if (isRouteErrorResponse(error)) {
    heading = `${error.status} - ${error.statusText}`;
    message = error.data;
  }

  return (
    <html>
      <head>
        <Meta />
        <Links />
        <FontAwesome />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </head>
      <body>
        <SiteHeader simple={true} />
        <div className={`Primary`}>
          <div className="container">
            <div style={{ margin: "2em 0", minHeight: "200px" }}>
              <div>
                <h2>{heading}</h2>
              </div>
              <div>
                <p>{message}</p>
              </div>
            </div>
          </div>
        </div>
        <SiteFooter />
        <ScrollRestoration />
        <Scripts />
        <LiveReload port={8002} />
      </body>
    </html>);

}

export const headers: HeadersFunction = ({
  actionHeaders,
  errorHeaders,
  loaderHeaders,
  parentHeaders
}) => ({
  "Access-Control-Allow-Origin": "*"
});

export default function App() {
  const activeClassName = "nav-link-active";
  const { ident, requestUrl, ENV } = useBindings<typeof loader>();
  const isContribute = requestUrl.includes("/contribute");

  /* this makes apple-developer-merchantid-domain-association route work. I dislike it but but its necessary  */
  if (requestUrl.includes("/.well-known/apple-developer-merchantid-domain-association")) {
    return <Outlet />;
  }

  return (
    <html lang="en">
      <head>
        <Meta />
        <Links />
        <FontAwesome />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </head>
      <body className={`${isContribute ? "page-contribute" : "page-default"}`}>
        <PublicEnv {...ENV} />
        <GTM {...ENV} />
        <Parsely />
        <SiteHeader ident={ident} requestUrl={requestUrl} />
        <nav className="nav" style={{ display: "none" }}>
          <NavLink
            to={"index"}
            className={({ isActive }: {isActive: boolean;}) =>
            isActive ? activeClassName : undefined}>

            Index
          </NavLink>
          <NavLink
            to={`account`}
            className={({ isActive }: {isActive: boolean;}) =>
            isActive ? activeClassName : undefined}>

            Account
          </NavLink>
          <NavLink
            to={`membership`}
            className={({ isActive }: {isActive: boolean;}) =>
            isActive ? activeClassName : undefined}>

            Membership
          </NavLink>
          <NavLink
            to={`contribute`}
            className={({ isActive }: {isActive: boolean;}) =>
            isActive ? activeClassName : undefined}>

            Contribute
          </NavLink>
          <NavLink
            to={`petition`}
            className={({ isActive }: {isActive: boolean;}) =>
            isActive ? activeClassName : undefined}>

            Petition
          </NavLink>
        </nav>
        <div className={`${isContribute ? "" : "Primary"}`}>
          <Outlet />
        </div>
        <SiteFooter requestURL={requestUrl} />
        <ScrollRestoration />
        <Scripts />
        <LiveReload />
      </body>
    </html>);

}